import { KeyboardEvent } from "react";
import { flexRender, Header, SortingState } from "@tanstack/react-table";
import { TableCell } from "@mui/material";
import { twMerge } from "tailwind-merge";

import { getSortIcon } from "ui/data-grid/helpers/get-sort-icon";

import "ui/data-grid/data-grid.types";

type TableHeaderCellProps<T> = {
  header: Header<T, unknown>;
  sortingParams: SortingState;
  isMultiSort: boolean;
};

export const TableHeaderCell = <T,>({
  header,
  sortingParams,
  isMultiSort,
}: TableHeaderCellProps<T>) => {
  const { column } = header;
  const isColumnSorted = column.getSortIndex() >= 0;
  const isColumnSortable = column.columnDef.enableSorting;
  const sortDirection = sortingParams[0]?.desc ? "descending" : "ascending";
  const toggleSorting = column.getToggleSortingHandler();

  const headerPositionClass = isMultiSort
    ? "justify-between"
    : column.columnDef.meta?.isHeaderCentered && "justify-center";

  const handleKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      toggleSorting?.(event);
    }
  };

  return (
    <TableCell
      component="th"
      scope="col"
      variant="head"
      onClick={toggleSorting}
      onKeyDown={handleKeyDown}
      tabIndex={isColumnSortable ? 0 : -1}
      aria-sort={isColumnSortable ? sortDirection : undefined}
      aria-label={
        isColumnSortable
          ? `${column.columnDef.header}, sorted by ${sortDirection}`
          : `Column ${column.columnDef.header} is not sortable`
      }
      sx={{ cursor: isColumnSortable ? "pointer" : "default" }}
      className={twMerge("py-3", column.columnDef.meta?.headerClassName)}>
      <div className={twMerge("flex items-center", headerPositionClass)}>
        <div className="flex items-center">
          <div className="mr-1 text-gray-900">
            {flexRender(column.columnDef.header, header.getContext())}
          </div>
          {getSortIcon(sortingParams, column.columnDef.id || "", column.columnDef.enableSorting)}
        </div>

        {isMultiSort && isColumnSorted && (
          <div className="ml-2 rounded-full bg-aqua-50 px-1 text-xs text-aqua-600">
            {column.getSortIndex() + 1}
          </div>
        )}
      </div>
    </TableCell>
  );
};
